/**
 * Course Configuration — plan LCM / Workshop templates from Topics → Subtopics.
 *
 * LCM: Add session on a topic → Live Class Mandatory
 *   (includes that topic + free topics above it).
 * Workshops: always last — Add Online/Onsite Workshop buttons.
 *   → includes ALL subject topics, split across 4 days by default.
 */

import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { ChevronDown, ChevronRight, Plus, Trash2, X } from 'lucide-react';
import {
  buildCoursePlanSessionKey,
  buildCoursePlanSessionTitle,
  LIVE_SESSION_CATEGORY_PREFIX,
} from '../../../codelive/create/utils/liveSessionNaming';
import type { SessionCategory } from '../../../codelive/create/utils/scheduledSessionStorage';

export type CourseLiveSessionCategory =
  | 'mandatory'
  | 'workshop_online'
  | 'workshop_onsite';

export type CoursePlannedTopic = {
  id: string;
  name: string;
  subtopics: { id: string; name: string }[];
};

export type CoursePlannedLiveSession = {
  id: string;
  category: CourseLiveSessionCategory | '';
  /** Batch-agnostic title, e.g. "LCM SQL - 001" (period added when creating for a batch). */
  title: string;
  /** Stable key for per-batch create: "lcm:sql:001" */
  sessionKey: string;
  namePrefix: string;
  sequence: number;
  durationMinutes: number;
  source: 'topic' | 'subtopic' | 'workshop';
  topicId: string;
  subtopicId?: string;
  topics: CoursePlannedTopic[];
  workshopDayCount?: number;
  dayTopics?: CoursePlannedTopic[][];
};

type PlanSubtopic = {
  id: string;
  subtopicName?: string;
  name?: string;
};

type PlanTopic = {
  id: string;
  name: string;
  subtopics: PlanSubtopic[];
};

type LocationPlanState = {
  selectedItems: {
    subject_id: string;
    subject_name: string;
    course: { course_id: string; course_name?: string };
    topics: PlanTopic[];
  };
  subjectPlan?: string;
  time?: number;
  liveSessions?: CoursePlannedLiveSession[];
  addLiveSessionForCourse?: boolean;
};

/** Topic-row action creates LCM only — workshops use footer buttons. */
const WORKSHOP_OPTIONS: {
  id: 'workshop_online' | 'workshop_onsite';
  label: string;
}[] = [
  { id: 'workshop_online', label: 'Online Workshop' },
  { id: 'workshop_onsite', label: 'Onsite Workshop' },
];

/** Aligns with create wizard DEFAULT / full-day workshop (8h per day). */
export const DEFAULT_WORKSHOP_DAYS = 4;
const WORKSHOP_MINUTES_PER_DAY = 480;

function newId(): string {
  return `cls_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeTopic(topic: PlanTopic): CoursePlannedTopic {
  return {
    id: String(topic.id),
    name: String(topic.name || topic.id),
    subtopics: (topic.subtopics || []).map((sub, i) => ({
      id: String(sub.id || `sub-${i}`),
      name: String(sub.subtopicName || sub.name || `Subtopic ${i + 1}`),
    })),
  };
}

function typeLabel(category: string): string {
  if (category === 'mandatory') return 'Live Class Mandatory';
  const w = WORKSHOP_OPTIONS.find((t) => t.id === category);
  return w?.label || category;
}

function isWorkshopCategory(
  category: string,
): category is 'workshop_online' | 'workshop_onsite' {
  return category === 'workshop_online' || category === 'workshop_onsite';
}

/** Same prefixes as create wizard: LCM / LW-Online / LW-Onsite. */
function categoryPrefix(category: CourseLiveSessionCategory | ''): string {
  if (!category) return 'LCM';
  return (
    LIVE_SESSION_CATEGORY_PREFIX[category as SessionCategory] ||
    (category === 'mandatory' ? 'LCM' : String(category))
  );
}

/**
 * Course-plan template title (no batch period).
 * Final batch session name via instantiatePlannedSessionName(title, period)
 * e.g. "LCM SQL - 001" → "LCM SQL - 001 - 7M 2026".
 */
export function buildPlannedSessionTitle(
  category: CourseLiveSessionCategory,
  subjectIdOrName: string,
  sequence: number,
): string {
  return buildCoursePlanSessionTitle(
    category as SessionCategory,
    subjectIdOrName,
    sequence,
  );
}

export function buildPlannedSessionKey(
  category: CourseLiveSessionCategory,
  subjectIdOrName: string,
  sequence: number,
): string {
  return buildCoursePlanSessionKey(
    category as SessionCategory,
    subjectIdOrName,
    sequence,
  );
}

function nextSequenceForCategory(
  sessions: CoursePlannedLiveSession[],
  category: CourseLiveSessionCategory,
): number {
  const used = sessions
    .filter((s) => s.category === category)
    .map((s) => Number(s.sequence) || 0)
    .filter((n) => n > 0);
  let seq = 1;
  const set = new Set(used);
  while (set.has(seq)) seq += 1;
  return seq;
}

function orderTopicsByPlan(
  topics: CoursePlannedTopic[],
  planOrder: CoursePlannedTopic[],
): CoursePlannedTopic[] {
  const index = new Map(planOrder.map((t, i) => [t.id, i]));
  return [...topics].sort(
    (a, b) => (index.get(a.id) ?? 0) - (index.get(b.id) ?? 0),
  );
}

/**
 * Split topics across workshop days in plan order (contiguous chunks).
 * Same idea as create-wizard dayTopics[][] tagged by workshopDayIndex.
 */
export function splitTopicsIntoWorkshopDays(
  topics: CoursePlannedTopic[],
  dayCount: number = DEFAULT_WORKSHOP_DAYS,
): CoursePlannedTopic[][] {
  const days = Math.max(1, dayCount);
  const result: CoursePlannedTopic[][] = Array.from({ length: days }, () => []);
  if (!topics.length) return result;

  const perDay = Math.ceil(topics.length / days);
  topics.forEach((topic, i) => {
    const dayIndex = Math.min(Math.floor(i / perDay), days - 1);
    result[dayIndex].push({
      ...topic,
      subtopics: [...topic.subtopics],
    });
  });
  return result;
}

const CourseLiveSessionsPlan: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const planState = location.state as LocationPlanState | null;

  const allTopics = useMemo(
    () => (planState?.selectedItems?.topics || []).map(normalizeTopic),
    [planState],
  );

  const [addLiveSessionForCourse, setAddLiveSessionForCourse] = useState(() => {
    if (typeof planState?.addLiveSessionForCourse === 'boolean') {
      return planState.addLiveSessionForCourse;
    }
    return (planState?.liveSessions?.length ?? 0) > 0;
  });

  const [sessions, setSessions] = useState<CoursePlannedLiveSession[]>(() => {
    const subjectCodeSource =
      planState?.selectedItems?.subject_id ||
      planState?.selectedItems?.subject_name ||
      'SESSION';
    const byCategorySeq: Record<string, number> = {};
    return (planState?.liveSessions || []).map((s) => {
      const category = (s.category || '') as CourseLiveSessionCategory | '';
      const topics = Array.isArray(s.topics) ? s.topics : [];
      const isWs = isWorkshopCategory(category);
      const dayCount =
        s.workshopDayCount || (isWs ? DEFAULT_WORKSHOP_DAYS : undefined);
      const dayTopics =
        s.dayTopics ||
        (isWs && dayCount
          ? splitTopicsIntoWorkshopDays(topics, dayCount)
          : undefined);
      const catKey = category || 'mandatory';
      byCategorySeq[catKey] = (byCategorySeq[catKey] || 0) + 1;
      const sequence =
        Number(s.sequence) > 0 ? Number(s.sequence) : byCategorySeq[catKey];
      const namePrefix =
        s.namePrefix ||
        categoryPrefix((category || 'mandatory') as CourseLiveSessionCategory);
      const title =
        s.title && String(s.title).match(/^(LCM|LCO|LW-Online|LW-Onsite)\s+/i)
          ? s.title
          : category
            ? buildPlannedSessionTitle(
                category as CourseLiveSessionCategory,
                subjectCodeSource,
                sequence,
              )
            : s.title || `Session ${sequence}`;
      const sessionKey =
        s.sessionKey ||
        (category
          ? buildPlannedSessionKey(
              category as CourseLiveSessionCategory,
              subjectCodeSource,
              sequence,
            )
          : `session:${sequence}`);
      return {
        ...s,
        category,
        title,
        sessionKey,
        namePrefix,
        sequence,
        topics,
        durationMinutes:
          s.durationMinutes ||
          (isWs
            ? WORKSHOP_MINUTES_PER_DAY * (dayCount || DEFAULT_WORKSHOP_DAYS)
            : 60),
        source: s.source || (isWs ? 'workshop' : 'topic'),
        topicId: s.topicId || topics?.[0]?.id || '',
        subtopicId: s.subtopicId,
        workshopDayCount: dayCount,
        dayTopics,
      };
    });
  });

  const [expandedTopicIds, setExpandedTopicIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [expandedSessionIds, setExpandedSessionIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [addingTopicsSessionId, setAddingTopicsSessionId] = useState<
    string | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  const lcmSessions = useMemo(
    () => sessions.filter((s) => s.category === 'mandatory'),
    [sessions],
  );
  const workshopSessions = useMemo(
    () => sessions.filter((s) => isWorkshopCategory(String(s.category))),
    [sessions],
  );

  /** Only LCM claims topics in the main list (workshops cover all topics separately). */
  const sessionByTopicId = useMemo(() => {
    const map = new Map<string, CoursePlannedLiveSession>();
    for (const s of lcmSessions) {
      for (const t of s.topics || []) {
        map.set(t.id, s);
      }
      if (s.topicId && !map.has(s.topicId)) map.set(s.topicId, s);
    }
    return map;
  }, [lcmSessions]);

  const sessionAnchorTopicId = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of lcmSessions) {
      const ordered = orderTopicsByPlan(s.topics || [], allTopics);
      if (ordered[0]) map.set(s.id, ordered[0].id);
    }
    return map;
  }, [lcmSessions, allTopics]);

  const availableTopicsForLcm = useMemo(
    () => allTopics.filter((t) => !sessionByTopicId.has(t.id)),
    [allTopics, sessionByTopicId],
  );

  if (!planState?.selectedItems) {
    navigate('/admin/course-configuration', { replace: true });
    return null;
  }

  const subjectName = planState.selectedItems.subject_name || '—';
  const subjectCodeSource =
    planState.selectedItems.subject_id ||
    planState.selectedItems.subject_name ||
    'SESSION';

  const toggleExpand = (topicId: string) => {
    setExpandedTopicIds((prev) => {
      const next = new Set(prev);
      if (next.has(topicId)) next.delete(topicId);
      else next.add(topicId);
      return next;
    });
  };

  const toggleSessionExpand = (sessionId: string) => {
    setExpandedSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const createLcmSession = (topic: CoursePlannedTopic) => {
    const topicIndex = allTopics.findIndex((t) => t.id === topic.id);
    if (topicIndex < 0) return;

    const included = allTopics
      .slice(0, topicIndex + 1)
      .filter((t) => !sessionByTopicId.has(t.id))
      .map((t) => ({ ...t, subtopics: [...t.subtopics] }));

    if (!included.length) return;

    const id = newId();
    setSessions((prev) => {
      const sequence = nextSequenceForCategory(prev, 'mandatory');
      const title = buildPlannedSessionTitle(
        'mandatory',
        subjectCodeSource,
        sequence,
      );
      const sessionKey = buildPlannedSessionKey(
        'mandatory',
        subjectCodeSource,
        sequence,
      );
      const lcmSessions = prev.filter((s) => !isWorkshopCategory(String(s.category)));
      const workshops = prev.filter((s) => isWorkshopCategory(String(s.category)));
      return [
        ...lcmSessions,
        {
          id,
          category: 'mandatory' as const,
          title,
          sessionKey,
          namePrefix: 'LCM',
          sequence,
          durationMinutes: 60,
          source: 'topic' as const,
          topicId: included[0].id,
          topics: included,
        },
        ...workshops,
      ];
    });
    setExpandedSessionIds((prev) => new Set(prev).add(id));
    setAddingTopicsSessionId(null);
  };

  const addTopicToLcmSession = (
    sessionId: string,
    topic: CoursePlannedTopic,
  ) => {
    setError(null);
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== sessionId || s.category !== 'mandatory') return s;
        if (s.topics.some((t) => t.id === topic.id)) return s;
        const topics = orderTopicsByPlan(
          [...s.topics, { ...topic, subtopics: [...topic.subtopics] }],
          allTopics,
        );
        return {
          ...s,
          topics,
          topicId: topics[0]?.id || s.topicId,
          // Keep LCM {subject} - NNN title; topics shown in card body
        };
      }),
    );
    setExpandedSessionIds((prev) => new Set(prev).add(sessionId));
  };

  const addLcmSessionFromTopic = (topic: CoursePlannedTopic) => {
    if (sessionByTopicId.has(topic.id)) {
      setError('This topic is already in a live class session.');
      return;
    }
    setError(null);
    createLcmSession(topic);
  };

  const addWorkshop = (category: 'workshop_online' | 'workshop_onsite') => {
    if (sessions.some((s) => s.category === category)) {
      setError(`${typeLabel(category)} is already added.`);
      return;
    }
    if (!allTopics.length) {
      setError('No topics in the subject plan to include in the workshop.');
      return;
    }

    const dayTopics = splitTopicsIntoWorkshopDays(
      allTopics,
      DEFAULT_WORKSHOP_DAYS,
    );
    const topics = dayTopics.flat();
    const id = newId();
    const sequence = nextSequenceForCategory(sessions, category);
    const title = buildPlannedSessionTitle(
      category,
      subjectCodeSource,
      sequence,
    );
    const sessionKey = buildPlannedSessionKey(
      category,
      subjectCodeSource,
      sequence,
    );

    setError(null);
    setSessions((prev) => [
      ...prev,
      {
        id,
        category,
        title,
        sessionKey,
        namePrefix: categoryPrefix(category),
        sequence,
        durationMinutes: WORKSHOP_MINUTES_PER_DAY * DEFAULT_WORKSHOP_DAYS,
        source: 'workshop',
        topicId: topics[0]?.id || '',
        topics,
        workshopDayCount: DEFAULT_WORKSHOP_DAYS,
        dayTopics,
      },
    ]);
    setExpandedSessionIds((prev) => new Set(prev).add(id));
  };

  const removeSession = (sessionId: string) => {
    setError(null);
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    setExpandedSessionIds((prev) => {
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });
    if (addingTopicsSessionId === sessionId) setAddingTopicsSessionId(null);
  };

  const openAddTopics = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setError(null);
    setExpandedSessionIds((prev) => new Set(prev).add(sessionId));
    setAddingTopicsSessionId((prev) =>
      prev === sessionId ? null : sessionId,
    );
  };

  const goBack = () => {
    navigate('/admin/course-configuration', {
      state: {
        selectedItems: planState.selectedItems,
        subjectPlan: planState.subjectPlan,
        time: planState.time,
        liveSessions: sessions,
        addLiveSessionForCourse,
      },
    });
  };

  const goConfirm = () => {
    // ---------------------------------------------------------------
    // step 1: Require a session only when the course opts in
    // ---------------------------------------------------------------
    if (addLiveSessionForCourse && !sessions.length) {
      setError(
        'Add at least one Live Class (Add session) or a workshop.',
      );
      return;
    }
    const plannedSessions = addLiveSessionForCourse ? sessions : [];
    navigate('/admin/confirm-subject-plan', {
      state: {
        selectedItems: planState.selectedItems,
        subjectPlan: planState.subjectPlan,
        time: planState.time,
        liveSessions: plannedSessions,
        liveSessionCount: addLiveSessionForCourse ? lcmSessions.length : 0,
        workshopCount: addLiveSessionForCourse ? workshopSessions.length : 0,
        addLiveSessionForCourse,
      },
    });
  };

  const ROW_GAP = 12;
  const CHEVRON_WIDTH = 24;
  const ACTION_BTN_WIDTH = 118;
  const ICON_BTN_SIZE = 32;
  const CONTROLS_GAP = 8;
  const CONTROLS_WIDTH = ACTION_BTN_WIDTH + CONTROLS_GAP + ICON_BTN_SIZE;

  const rowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `${CHEVRON_WIDTH}px minmax(0, 1fr) ${CONTROLS_WIDTH}px`,
    columnGap: ROW_GAP,
    alignItems: 'center',
    minHeight: 44,
    padding: '8px 16px',
  };

  const contentPadLeft = 16 + CHEVRON_WIDTH + ROW_GAP;

  const controlsRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: CONTROLS_GAP,
    width: '100%',
  };

  const actionBtnStyle: React.CSSProperties = {
    fontSize: '12px',
    padding: '4px 10px',
    width: ACTION_BTN_WIDTH,
    height: ICON_BTN_SIZE,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  };

  const iconBtnStyle: React.CSSProperties = {
    fontSize: '12px',
    padding: 0,
    width: ICON_BTN_SIZE,
    height: ICON_BTN_SIZE,
    flexShrink: 0,
  };

  const renderTopicControls = (topic: CoursePlannedTopic) => (
    <div style={controlsRowStyle}>
      <button
        type="button"
        className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center"
        style={actionBtnStyle}
        title="Add Live Class Mandatory session (this topic + free topics above)"
        onClick={() => addLcmSessionFromTopic(topic)}
      >
        <Plus size={14} className="me-1" />
        Add session
      </button>
      <span style={{ width: ICON_BTN_SIZE, flexShrink: 0 }} aria-hidden />
    </div>
  );

  const renderLcmSessionCard = (session: CoursePlannedLiveSession) => {
    const open = expandedSessionIds.has(session.id);
    const showAddPicker = addingTopicsSessionId === session.id;
    const ordered = orderTopicsByPlan(session.topics || [], allTopics);
    const topicCount = ordered.length;

    return (
      <div
        key={session.id}
        className="rounded-2 bg-white"
        style={{
          border: '1px solid #dee2e6',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div
          style={{ ...rowStyle, cursor: 'pointer' }}
          onClick={() => toggleSessionExpand(session.id)}
        >
          <div className="d-flex align-items-center justify-content-center text-secondary">
            {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          <div
            className="d-flex align-items-center gap-2 text-truncate"
            style={{ minWidth: 0 }}
          >
            <span
              className="badge bg-warning text-dark flex-shrink-0"
              style={{
                fontSize: 11,
                padding: '5px 8px',
                borderRadius: 4,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
              title={session.sessionKey}
            >
              {session.namePrefix || 'LCM'}
            </span>
            <span className="text-dark text-truncate fw-medium" style={{ fontSize: 13 }}>
              {session.title}
              <span className="text-muted fw-normal ms-1">
                · {topicCount} topic{topicCount === 1 ? '' : 's'}
              </span>
            </span>
          </div>

          <div style={controlsRowStyle} onClick={(e) => e.stopPropagation()}>
            <div
              className="d-flex align-items-center justify-content-end"
              style={{ width: ACTION_BTN_WIDTH, flexShrink: 0 }}
            >
              <button
                type="button"
                className={`btn btn-sm d-inline-flex align-items-center justify-content-center ${
                  showAddPicker ? 'btn-outline-secondary' : 'btn-outline-primary'
                }`}
                style={showAddPicker ? iconBtnStyle : actionBtnStyle}
                title={showAddPicker ? 'Stop adding topics' : 'Add topics'}
                onClick={(e) => openAddTopics(session.id, e)}
              >
                {showAddPicker ? (
                  <X size={14} />
                ) : (
                  <>
                    <Plus size={14} className="me-1" />
                    Add topics
                  </>
                )}
              </button>
            </div>
            <button
              type="button"
              className="btn btn-outline-danger btn-sm d-inline-flex align-items-center justify-content-center"
              style={iconBtnStyle}
              title="Remove session"
              onClick={(e) => {
                e.stopPropagation();
                removeSession(session.id);
              }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {open ? (
          <div className="border-top pb-2 pt-1">
            {ordered.map((t) => (
              <div
                key={t.id}
                className="py-2 pe-3"
                style={{ paddingLeft: contentPadLeft }}
              >
                <div className="fw-semibold text-dark" style={{ fontSize: 14 }}>
                  {t.name}
                  <span className="text-muted small fw-normal ms-1">
                    ({t.subtopics.length} subtopic
                    {t.subtopics.length === 1 ? '' : 's'})
                  </span>
                </div>
                {t.subtopics.length > 0 ? (
                  <div className="text-secondary mt-1" style={{ fontSize: 13 }}>
                    {t.subtopics.map((s) => s.name).join(', ')}
                  </div>
                ) : null}
              </div>
            ))}

            {showAddPicker ? (
              <div
                className="mt-2 mb-1 me-3 p-2 rounded-2"
                style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  marginLeft: contentPadLeft,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="small fw-medium mb-2">
                  Available topics ({availableTopicsForLcm.length})
                </div>
                {availableTopicsForLcm.length === 0 ? (
                  <div className="text-muted small">No free topics left.</div>
                ) : (
                  <div className="d-flex flex-column gap-1">
                    {availableTopicsForLcm.map((t) => (
                      <div
                        key={t.id}
                        className="d-flex align-items-center justify-content-between gap-2 py-1 px-2 rounded bg-white border"
                      >
                        <div
                          style={{ fontSize: 13, minWidth: 0 }}
                          className="text-truncate"
                        >
                          {t.name}
                          <span className="text-muted small ms-1">
                            ({t.subtopics.length})
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: 28, height: 28, padding: 0 }}
                          title="Add to this session"
                          onClick={() => addTopicToLcmSession(session.id, t)}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const renderWorkshopCard = (session: CoursePlannedLiveSession) => {
    const open = expandedSessionIds.has(session.id);
    const dayTopics =
      session.dayTopics ||
      splitTopicsIntoWorkshopDays(
        session.topics || [],
        session.workshopDayCount || DEFAULT_WORKSHOP_DAYS,
      );
    const dayCount = session.workshopDayCount || dayTopics.length;

    return (
      <div
        key={session.id}
        className="rounded-2 bg-white"
        style={{
          border: '1px solid #dee2e6',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div
          style={{ ...rowStyle, cursor: 'pointer' }}
          onClick={() => toggleSessionExpand(session.id)}
        >
          <div className="d-flex align-items-center justify-content-center text-secondary">
            {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>

          <div
            className="d-flex align-items-center gap-2 text-truncate"
            style={{ minWidth: 0 }}
          >
            <span
              className="badge text-bg-info flex-shrink-0"
              style={{
                fontSize: 11,
                padding: '5px 8px',
                borderRadius: 4,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
              title={session.sessionKey}
            >
              {session.namePrefix || typeLabel(session.category)}
            </span>
            <span className="text-dark text-truncate fw-medium" style={{ fontSize: 13 }}>
              {session.title}
              <span className="text-muted fw-normal ms-1">
                · {dayCount} days · {(session.topics || []).length} topics
              </span>
            </span>
          </div>

          <div style={controlsRowStyle} onClick={(e) => e.stopPropagation()}>
            <span style={{ width: ACTION_BTN_WIDTH, flexShrink: 0 }} aria-hidden />
            <button
              type="button"
              className="btn btn-outline-danger btn-sm d-inline-flex align-items-center justify-content-center"
              style={iconBtnStyle}
              title="Remove workshop"
              onClick={(e) => {
                e.stopPropagation();
                removeSession(session.id);
              }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {open ? (
          <div className="border-top pb-2 pt-1">
            {dayTopics.map((dayTopicsList, dayIndex) => (
              <div
                key={`day-${dayIndex}`}
                className="py-2 pe-3"
                style={{ paddingLeft: contentPadLeft }}
              >
                <div
                  className="fw-semibold text-dark mb-1"
                  style={{ fontSize: 13 }}
                >
                  Day {dayIndex + 1}
                  <span className="text-muted small fw-normal ms-1">
                    ({dayTopicsList.length} topic
                    {dayTopicsList.length === 1 ? '' : 's'})
                  </span>
                </div>
                {dayTopicsList.length === 0 ? (
                  <div className="text-muted small">No topics</div>
                ) : (
                  dayTopicsList.map((t) => (
                    <div key={t.id} className="mb-2 ms-1">
                      <div style={{ fontSize: 13 }} className="text-dark">
                        {t.name}
                        <span className="text-muted small ms-1">
                          ({t.subtopics.length} subtopic
                          {t.subtopics.length === 1 ? '' : 's'})
                        </span>
                      </div>
                      {t.subtopics.length > 0 ? (
                        <div className="text-secondary" style={{ fontSize: 12 }}>
                          {t.subtopics.map((s) => s.name).join(', ')}
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className="border pt-3 px-2 rounded-2 bg-white my-2 me-2"
      style={{ height: 'calc(100vh - 90px)', overflowY: 'auto' }}
    >
      <div className="container-fluid mt-2" style={{ width: 'calc(100% - 5px)' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Live Sessions & Workshops</h4>
          <div className="d-flex gap-2">
            <Button variant="secondary" size="sm" onClick={goBack}>
              Back
            </Button>
            <Button variant="primary" size="sm" onClick={goConfirm}>
              Continue
            </Button>
          </div>
        </div>

        <label
          className="d-flex align-items-center gap-2 mb-3"
          style={{ fontSize: 14, cursor: 'pointer', width: 'fit-content' }}
        >
          <input
            type="checkbox"
            checked={addLiveSessionForCourse}
            onChange={(e) => {
              setAddLiveSessionForCourse(e.target.checked);
              setError(null);
            }}
          />
          Add live session for the course
        </label>

        {error ? <div className="alert alert-danger py-2">{error}</div> : null}

        <div className="mb-3 p-0">
          <h5 className="text-dark fw-semibold mb-0" style={{ color: '#495057' }}>
            {subjectName}
          </h5>
          <hr
            className="mt-2 mb-3 p-0"
            style={{ borderColor: '#e9ecef', borderWidth: '1px' }}
          />
        </div>

        {allTopics.length === 0 ? (
          <p className="text-muted">No topics in the subject plan.</p>
        ) : (
          <div className="d-flex flex-column gap-2 mb-4">
            {allTopics.map((topic) => {
              const owningSession = sessionByTopicId.get(topic.id);

              if (owningSession) {
                const anchorId = sessionAnchorTopicId.get(owningSession.id);
                if (anchorId !== topic.id) return null;
                return renderLcmSessionCard(owningSession);
              }

              const open = expandedTopicIds.has(topic.id);

              return (
                <div
                  key={topic.id}
                  className="rounded-2 bg-white"
                  style={{
                    border: '1px solid #dee2e6',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}
                >
                  <div style={rowStyle}>
                    <div className="d-flex align-items-center justify-content-center">
                      <button
                        type="button"
                        className="btn btn-link p-0 text-secondary lh-1"
                        onClick={() => toggleExpand(topic.id)}
                        aria-label={open ? 'Collapse' : 'Expand'}
                      >
                        {open ? (
                          <ChevronDown size={18} />
                        ) : (
                          <ChevronRight size={18} />
                        )}
                      </button>
                    </div>
                    <div
                      className="fw-semibold text-dark text-truncate"
                      style={{ cursor: 'pointer', fontSize: 14, minWidth: 0 }}
                      onClick={() => toggleExpand(topic.id)}
                      title={topic.name}
                    >
                      {topic.name}
                      <span className="text-muted small fw-normal ms-1">
                        ({topic.subtopics.length} subtopic
                        {topic.subtopics.length === 1 ? '' : 's'})
                      </span>
                    </div>
                    {renderTopicControls(topic)}
                  </div>

                  {open ? (
                    <div className="border-top">
                      {topic.subtopics.length === 0 ? (
                        <div
                          className="py-2 pe-3 text-muted small"
                          style={{ paddingLeft: contentPadLeft }}
                        >
                          No subtopics
                        </div>
                      ) : (
                        topic.subtopics.map((sub) => (
                          <div
                            key={sub.id}
                            className="py-2 pe-3 border-top text-secondary"
                            style={{
                              paddingLeft: contentPadLeft,
                              fontSize: 13,
                            }}
                          >
                            {sub.name}
                          </div>
                        ))
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}

            {/* Workshops always last */}
            {workshopSessions.map((ws) => renderWorkshopCard(ws))}

            <div
              className="d-flex flex-wrap align-items-center gap-2 pt-2 mt-1"
              style={{ borderTop: '1px dashed #dee2e6' }}
            >
              <span className="text-muted small me-1">Add workshop:</span>
              {WORKSHOP_OPTIONS.map((opt) => {
                const exists = sessions.some((s) => s.category === opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    style={{ fontSize: 12 }}
                    disabled={exists || allTopics.length === 0}
                    title={
                      exists
                        ? `${opt.label} already added`
                        : `Add ${opt.label} (all topics, ${DEFAULT_WORKSHOP_DAYS} days)`
                    }
                    onClick={() => addWorkshop(opt.id)}
                  >
                    <Plus size={14} className="me-1" style={{ marginTop: -2 }} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseLiveSessionsPlan;
